import { MouseEvent as ReactMouseEvent, useMemo, useState } from "react";
import { Checkbox, Select, Slider } from "antd";
import { useRecoilValue } from "recoil";
import { DesignDto } from "../../generated/dashboard-client";
import { memberState } from "../../state/atoms";
import { isFreeUser } from "../../service/BillingService";
import { getSheetOptions } from "../../util/sheetLayouts";
import { SheetPrintRequest } from "../../api/DesignApi";
import ProIcon from "../icons/ProIcon";
import { DownloadType } from "./DesignCardMenu";
import "./SheetPrintOptions.scss";

type SheetLayoutMode = "sheet" | "single";

export function useSheetPrint(design: DesignDto | null) {
  const options = useMemo(() => getSheetOptions(design?.size), [design?.size]);
  const [layoutMode, setLayoutMode] = useState<SheetLayoutMode>("sheet");
  const [sheetKey, setSheetKey] = useState("letter");
  const [cropMarks, setCropMarks] = useState(false);

  const activeOption = options.find((it) => it.sheet === sheetKey) ?? options[0];
  const request: SheetPrintRequest | undefined =
    layoutMode === "sheet" && activeOption
      ? { sheet: activeOption.sheet, cropMarks }
      : undefined;

  return {
    options,
    layoutMode,
    setLayoutMode,
    activeSheet: activeOption?.sheet,
    setSheetKey,
    cropMarks,
    setCropMarks,
    request,
  };
}

export type SheetPrint = ReturnType<typeof useSheetPrint>;

const stopPropagation = (ev: ReactMouseEvent) => ev.stopPropagation();

interface SheetPrintOptionsProps {
  sheetPrint: SheetPrint;
}

export function SheetPrintOptions({ sheetPrint }: SheetPrintOptionsProps) {
  return (
    <div className={"sheet-options"} onClick={stopPropagation}>
      <label className={"option-label"}>Layout</label>
      <div className={"sheet-mode"} role={"group"} aria-label={"Layout"}>
        <button
          type={"button"}
          className={sheetPrint.layoutMode === "sheet" ? "active" : ""}
          onClick={() => sheetPrint.setLayoutMode("sheet")}
        >
          Fit to sheet
        </button>
        <button
          type={"button"}
          className={sheetPrint.layoutMode === "single" ? "active" : ""}
          onClick={() => sheetPrint.setLayoutMode("single")}
        >
          One per page
        </button>
      </div>

      {sheetPrint.layoutMode === "sheet" && (
        <>
          <label className={"option-label"}>Sheet size</label>
          <div className={"sheet-sizes"}>
            {sheetPrint.options.map((option) => (
              <button
                type={"button"}
                key={option.sheet}
                className={`sheet-size ${option.sheet === sheetPrint.activeSheet ? "active" : ""}`}
                onClick={() => sheetPrint.setSheetKey(option.sheet)}
              >
                <span
                  className={"sheet-preview"}
                  aria-hidden={"true"}
                  style={{
                    aspectRatio: `${option.sheetWidth} / ${option.sheetHeight}`,
                    gridTemplateColumns: `repeat(${option.columns}, 1fr)`,
                    gridTemplateRows: `repeat(${option.rows}, 1fr)`,
                  }}
                >
                  {Array.from({ length: option.perSheet }, (_, index) => (
                    <span key={index} />
                  ))}
                </span>
                <span className={"sheet-size-label"}>
                  <b>{option.sheetLabel}</b>
                  <span>{option.perSheet} per sheet</span>
                </span>
              </button>
            ))}
          </div>
          <div className={"crop-marks-checkbox"}>
            <Checkbox
              checked={sheetPrint.cropMarks}
              onChange={(ev) => sheetPrint.setCropMarks(ev.target.checked)}
            />
            <span>Crop marks</span>
          </div>
        </>
      )}
    </div>
  );
}

interface DownloadFileTypeSelectProps {
  design: DesignDto;
  downloadType: DownloadType;
  onChange: (type: DownloadType) => void;
  videoDuration: number;
  onVideoDurationChange: (duration: number) => void;
}

export function DownloadFileTypeSelect({
  design,
  downloadType,
  onChange,
  videoDuration,
  onVideoDurationChange,
}: DownloadFileTypeSelectProps) {
  const member = useRecoilValue(memberState);
  const showPro = isFreeUser(member) && !design.free;

  const fileTypes = [
    ...(design.hasVideo
      ? [{ type: DownloadType.HDVideo, title: "HD Video", subtitle: "High resolution video for displays and social media", pro: true }]
      : []),
    { type: DownloadType.Standard, title: "Standard PDF", subtitle: "High quality for in-house printing", pro: showPro },
    { type: DownloadType.ProPrint, title: "Pro Print PDF", subtitle: "High quality with bleed", pro: showPro },
    { type: DownloadType.PremiumWeb, title: "Premium Web Image", subtitle: "High quality PNG image for web", pro: showPro },
    { type: DownloadType.BasicImage, title: "Basic Image", subtitle: "Basic quality JPG image", pro: false, free: showPro },
  ];

  return (
    <div onClick={stopPropagation}>
      <label className={"option-label"}>File Type</label>
      <Select
        className={"file-type-select"}
        value={downloadType}
        onChange={onChange}
        optionLabelProp={"title"}
        getPopupContainer={(node) => node.parentElement!}
        options={fileTypes.map((it) => ({
          value: it.type,
          title: it.title,
          label: (
            <div>
              <div className={"option-title"}>
                {it.title}
                {it.pro && <ProIcon />}
                {it.free && <span className={"free"}>Free</span>}
              </div>
              <div className={"option-subtitle"}>{it.subtitle}</div>
            </div>
          ),
        }))}
      />
      {downloadType === DownloadType.HDVideo && (
        <div className="slider">
          <div>Duration</div>
          <Slider value={videoDuration} min={1} max={30} onChange={onVideoDurationChange} />
          <span>{videoDuration}s</span>
        </div>
      )}
    </div>
  );
}
